import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { storeTokens } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

// Module-level flag survives React StrictMode unmount/remount
let _authProcessed = false;

function extractSessionId() {
  // Try hash fragment first: /dashboard#session_id=xxx
  const hash = window.location.hash || '';
  if (hash.includes('session_id=')) {
    const raw = hash.split('session_id=')[1]?.split('&')[0];
    return raw ? decodeURIComponent(raw) : null;
  }
  // Try query params: /dashboard?session_id=xxx
  const params = new URLSearchParams(window.location.search);
  const qp = params.get('session_id');
  if (qp) return qp;
  // Try the full URL as last resort (some redirects merge hash and query)
  const fullUrl = window.location.href;
  const match = fullUrl.match(/session_id=([^&#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    if (_authProcessed) return;
    _authProcessed = true;

    const sessionId = extractSessionId();

    if (!sessionId) {
      console.error('No session_id found in URL:', window.location.href);
      _authProcessed = false;
      setStatus('No session found - redirecting to login...');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
      return;
    }

    console.log('Processing session_id, length:', sessionId.length);
    setStatus('Verifying your account...');

    api.post('/auth/google', { session_id: sessionId })
      .then(res => {
        storeTokens(res.data);
        setUser(res.data);
        _authProcessed = false;
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        console.error('Google auth failed:', err?.response?.status, err?.response?.data);
        _authProcessed = false;
        setStatus('Authentication failed. Redirecting...');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      });
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500">{status}</p>
      </div>
    </div>
  );
}
